import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export function CreatableCombobox({
  items = [], // string[] (initial list)
  value, // string | undefined
  onSelect, // (value: string) => void
  placeholder = "Select value...",
  classes = {},
}) {
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);

  // Local list so newly created values appear in dropdown
  const [localItems, setLocalItems] = React.useState(items);

  // Keep local list in sync if parent items change
  React.useEffect(() => {
    setLocalItems((prev) => {
      const merged = new Set(prev);
      items.forEach((item) => merged.add(item));
      return Array.from(merged);
    });
  }, [items]);

  const normalizedItems = React.useMemo(
    () => localItems.map((v) => v.toLowerCase()),
    [localItems],
  );

  const showCreate =
    search.trim().length > 0 && !normalizedItems.includes(search.toLowerCase());

  const handleSelect = (val) => {
    const trimmed = val.trim();
    if (!trimmed) return;

    // Insert locally if not already present
    if (!normalizedItems.includes(trimmed.toLowerCase())) {
      setLocalItems((prev) => [...prev, trimmed]);
    }

    onSelect(trimmed);
    setSearch("");
    setOpen(false); // Close dropdown after selection
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={`w-full justify-between ${classes?.button}`}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={`p-0 pointer-events-auto ${classes?.popoverContent}`}
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command>
          <CommandInput
            placeholder="Search or type new..."
            value={search}
            onValueChange={setSearch}
          />

          <CommandEmpty>No value found.</CommandEmpty>

          <div className="max-h-[200px] overflow-y-auto">
            <CommandGroup>
              {localItems.map((item) => (
                <CommandItem
                  key={item}
                  value={item}
                  onSelect={() => handleSelect(item)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === item ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {item}
                </CommandItem>
              ))}

              {showCreate && (
                <CommandItem
                  value={search}
                  onSelect={() => handleSelect(search)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {`Add \"${search}\"`}
                </CommandItem>
              )}
            </CommandGroup>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
